import { queryOptions, useQuery } from '@tanstack/react-query';

import useUserPid from '@/features/auth/hooks/use-user-pid';
import type { UserPid } from '@/features/auth/services/oidc';
import { getNextDetailsKey, getNextDetailsKeyRoute, type InstitutionDetailsKeyForDetailsType, type InstitutionDetailsType } from '@/features/institution/utils';
import { useProfileInstitutionDetailsAnswers } from '@/hooks/queries/use-profile-institution-details';
import type { InstitutionDetailsPartial } from '@/services/api/profile';

export const detailsNextQueryKey = (pid: UserPid, institutionDetails: InstitutionDetailsPartial | null | undefined) => [
  'institution',
  'next-details-route',
  pid,
  institutionDetails,
];

const detailsNextQueryFn = async <T extends InstitutionDetailsType>(detailType: T, institutionDetails: InstitutionDetailsPartial | null | undefined) => {
  const nextDetailsKey = await getNextDetailsKey(detailType, institutionDetails);
  const nextDetailsKeyRoute = await getNextDetailsKeyRoute(detailType, institutionDetails);

  return nextDetailsKey
    ? {
        key: nextDetailsKey as InstitutionDetailsKeyForDetailsType<T>,
        route: nextDetailsKeyRoute ? `/institution/${detailType}/${nextDetailsKeyRoute}` : undefined,
      }
    : null;
};

const detailsNextQueryOptions = <T extends InstitutionDetailsType>(
  pid: UserPid,
  detailType: T,
  institutionDetails: InstitutionDetailsPartial | null | undefined,
  enabled: boolean,
) =>
  queryOptions({
    enabled,
    gcTime: 0,
    staleTime: 0,
    queryFn: () => detailsNextQueryFn(detailType, institutionDetails),
    queryKey: detailsNextQueryKey(pid, institutionDetails),
  });

const useDetailsNext = <T extends InstitutionDetailsType>(detailType: T) => {
  const { pid } = useUserPid();
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useProfileInstitutionDetailsAnswers();
  const { isLoading: isLoadingQuery, data, ...rest } = useQuery(detailsNextQueryOptions(pid, detailType, institutionDetailsAnswers, !isLoadingInstitutionDetailsAnswers));
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingQuery;

  return {
    ...rest,
    data: data as { key: InstitutionDetailsKeyForDetailsType<T>; route: string } | null,
    isLoading,
  };
};

export { useDetailsNext };
